<template>
	<a-modal
		:visible="visible"
		:footer="null"
		:closable="false"
		:mask-closable="false"
		:destroy-on-close="true"
		width="640px"
		:centered="true"
		wrap-class-name="g-video-settings-modal"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<div class="g-video-settings">
			<div class="g-video-settings__header">
				<span class="g-video-settings__title">视频模块</span>
				<span class="g-video-settings__close mbm-iconfont mbm-guanbi1" @click="onCancel"></span>
			</div>

			<UploadProgressPanel
				v-if="isUploading"
				:tasks="uploadTasks"
				variant="modal"
			/>
			<template v-else>
				<div class="g-video-settings__field">
					<label class="g-video-settings__label">视频标题</label>
					<input
						v-model="form.title"
						type="text"
						class="g-video-settings__input"
						placeholder="请输入视频标题"
					/>
				</div>

				<div class="g-video-settings__player-box">
					<HeaderVideoPreview
						v-if="currentItem"
						:item="currentItem"
						layout="inline"
					/>
				</div>
			</template>

			<div class="g-video-settings__toolbar">
				<button
					type="button"
					class="g-video-settings__outline-btn"
					:disabled="isUploading"
					@click="onReupload"
				>
					重新上传
				</button>
			</div>

			<p v-if="!hideNotice" class="g-video-settings__notice">
				高清流量使用完后，视频可继续播放，但会变为低清晰度模式。
				<a href="javascript:;" class="g-video-settings__notice-link">查看说明</a>
				<a
					v-if="!hideDownloadSettings"
					href="javascript:;"
					class="g-video-settings__notice-link"
					@click.prevent="downloadSettingsVisible = true"
				>下载设置</a>
			</p>

			<div class="g-video-settings__footer">
				<div class="g-video-settings__footer-actions">
					<button type="button" class="g-video-settings__btn is-cancel" @click="onCancel">取消</button>
					<button
						type="button"
						class="g-video-settings__btn is-confirm"
						:disabled="!form.items.length || isUploading"
						@click="onConfirm"
					>确认</button>
				</div>
			</div>
		</div>
		</div>

		<FileDownloadSettingsModal
			v-if="!hideDownloadSettings"
			v-model:visible="downloadSettingsVisible"
			:value="form.downloadSettings"
			@confirm="onDownloadSettingsConfirm"
		/>

		<input
			ref="fileInput"
			type="file"
			accept="video/*,.mp4,.mov,.avi,.flv,.mkv,.webm"
			class="g-video-settings__file-input"
			@change="onFileChange"
		/>
	</a-modal>
</template>

<script>
	import HeaderVideoPreview from './HeaderVideoPreview.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import FileDownloadSettingsModal from './FileDownloadSettingsModal.vue';
	import {
		createDefaultFileDownloadSettings,
		normalizeFileDownloadSettings,
	} from '../utils/localFile.js';
	import {
		filterValidLocalVideoList,
		normalizeHeaderVideos,
		normalizeLocalVideos,
		revokeLocalVideoItem,
		revokeLocalVideos,
	} from '../utils/localVideo.js';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GVideoSettingsModal',
		components: { HeaderVideoPreview, UploadProgressPanel, FileDownloadSettingsModal },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			value: {
				type: Object,
				default: () => ({
					title: '',
					items: [],
					downloadSettings: createDefaultFileDownloadSettings(),
				}),
			},
			hideNotice: {
				type: Boolean,
				default: false,
			},
			hideDownloadSettings: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: {
					title: '',
					items: [],
					downloadSettings: createDefaultFileDownloadSettings(),
				},
				skipNextInit: false,
				isUploading: false,
				uploadTasks: [],
				downloadSettingsVisible: false,
			};
		},
		computed: {
			currentItem() {
				return this.form.items[0] || null;
			},
		},
		watch: {
			visible(value) {
				if (value) {
					if (this.skipNextInit) {
						this.skipNextInit = false;
						return;
					}
					this.initForm();
				}
			},
		},
		beforeUnmount() {
			revokeLocalVideos(this.form.items);
		},
		methods: {
			initForm() {
				const source = normalizeHeaderVideos(this.value);
				const incoming = source.items;
				const incomingIds = new Set(incoming.map((item) => item.id));
				this.form.items.forEach((item) => {
					if (!incomingIds.has(item.id)) {
						revokeLocalVideoItem(item);
					}
				});
				this.form = {
					title: source.title,
					items: incoming,
					downloadSettings: { ...source.downloadSettings },
				};
			},
			async setVideoFile(file) {
				const validFiles = filterValidLocalVideoList([file]);
				if (!validFiles.length) return;
				const prevFilename = this.form.items[0]?.filename || '';
				await this.uploadFiles(validFiles, (items) => {
					revokeLocalVideos(this.form.items);
					this.form.items = items;
					if (!this.form.title || this.form.title === prevFilename) {
						this.form.title = items[0]?.filename || '';
					}
				});
			},
			async uploadFiles(files, onSuccess) {
				this.isUploading = true;
				this.uploadTasks = createUploadTasks(files);
				try {
					const items = await uploadHeaderMediaFiles(this, {
						type: 'video',
						files,
						onProgress: (payload) => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					onSuccess?.(items);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			onReupload() {
				this.$refs.fileInput.value = '';
				this.$refs.fileInput.click();
			},
			async onFileChange(event) {
				const file = (event.target.files || [])[0];
				event.target.value = '';
				if (!file) return;
				await this.setVideoFile(file);
			},
			openWithUploadedItems(items) {
				revokeLocalVideos(this.form.items);
				const normalizedItems = normalizeLocalVideos(items);
				this.form = {
					title: normalizedItems[0]?.filename || '',
					items: normalizedItems,
					downloadSettings: createDefaultFileDownloadSettings(),
				};
				this.skipNextInit = true;
				this.$emit('update:visible', true);
			},
			async openWithFile(file) {
				const validFiles = filterValidLocalVideoList([file]);
				if (!validFiles.length) return;
				revokeLocalVideos(this.form.items);
				this.form = {
					title: '',
					items: [],
					downloadSettings: createDefaultFileDownloadSettings(),
				};
				this.skipNextInit = true;
				this.$emit('update:visible', true);
				await this.uploadFiles(validFiles, (items) => {
					this.form.items = items;
					this.form.title = items[0]?.filename || '';
				});
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				if (!this.form.items.length) return;
				this.$emit('confirm', normalizeHeaderVideos(this.form));
				this.$emit('update:visible', false);
			},
			onDownloadSettingsConfirm(downloadSettings) {
				this.form.downloadSettings = normalizeFileDownloadSettings(downloadSettings);
			},
		},
	};
</script>

<style scoped lang="less">
	.g-video-settings__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}

	.g-video-settings__title {
		font-size: 18px;
		font-weight: 600;
		color: #333;
	}

	.g-video-settings__close {
		font-size: 16px;
		color: #999;
		cursor: pointer;
		line-height: 1;

		&:hover {
			color: #666;
		}
	}

	.g-video-settings__field {
		margin-bottom: 14px;
	}

	.g-video-settings__label {
		display: block;
		margin-bottom: 8px;
		font-size: 13px;
		color: #666;
		line-height: 1.4;
	}

	.g-video-settings__input {
		width: 100%;
		height: 36px;
		padding: 0 12px;
		border: 1px solid #d9d9d9;
		border-radius: 4px;
		font-size: 14px;
		color: #333;
		box-sizing: border-box;
		outline: none;

		&:focus {
			border-color: #285af9;
		}
	}

	.g-video-settings__player-box {
		border: 1px solid #eee;
		border-radius: 4px;
		background: #fff;
		overflow: hidden;
	}

	.g-video-settings__toolbar {
		display: flex;
		align-items: center;
		margin-top: 14px;
	}

	.g-video-settings__outline-btn {
		height: 32px;
		padding: 0 14px;
		border: 1px solid #d9d9d9;
		border-radius: 4px;
		background: #fff;
		font-size: 13px;
		color: #666;
		cursor: pointer;

		&:hover:not(:disabled) {
			border-color: #285af9;
			color: #285af9;
		}

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	.g-video-settings__notice {
		margin: 14px 0 0;
		font-size: 12px;
		color: #999;
		line-height: 1.6;
	}

	.g-video-settings__notice-link {
		color: #285af9;
		text-decoration: none;

		& + & {
			margin-left: 8px;
		}

		&:hover {
			text-decoration: underline;
		}
	}

	.g-video-settings__footer {
		display: flex;
		justify-content: flex-end;
		margin-top: 24px;
		padding-top: 16px;
		border-top: 1px solid #f0f0f0;
	}

	.g-video-settings__footer-actions {
		display: flex;
		gap: 10px;
	}

	.g-video-settings__btn {
		min-width: 72px;
		height: 32px;
		padding: 0 16px;
		border-radius: 4px;
		font-size: 14px;
		cursor: pointer;
		outline: none;

		&.is-cancel {
			border: 1px solid #d9d9d9;
			background: #fff;
			color: #333;

			&:hover {
				color: #5280ff;
    			border-color: #5280ff;
			}
		}

		&.is-confirm {
			border: none;
			background: #285af9;
			color: #fff;

			&:hover:not(:disabled) {
				background: #3868ff;
			}

			&:disabled {
				background: #3868ff;
				cursor: not-allowed;
			}
		}
	}

	.g-video-settings__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-video-settings-modal {
		.edit-modal-scroll();

		.ant-modal-body {
			padding: 20px 24px 18px;
		}
	}
</style>
